<!-- Copyright 2023-2025 ForgeRock AS. All Rights Reserved

Use of this code requires a commercial software license with ForgeRock AS
or with one of its affiliates. All use shall be exclusively subject
to such license between the licensee and ForgeRock AS. -->
<template>
  <BRow
    id="adminGroupSelection"
    role="radiogroup">
    <template
      v-for="(group, index) in adminGroups"
      :key="index">
      <div class="col-12 col-lg-6 mb-4">
        <FrCardCheckboxInput
          :value="selectedAdminGroups.includes(group.name)"
          :key="group.name"
          class="h-100"
          @change="setGroup($event, group.name)">
          <BMedia
            no-body>
            <div
              :class="`
                fr-icon-container
                d-flex
                align-items-center
                justify-content-center
                p-3
                mr-4
                rounded
                border
                border-${group.iconColor}`">
              <FrIcon
                :icon-class="`color-${group.iconColor} md-24`"
                :name="group.icon" />
            </div>
            <BMediaBody>
              <h5>
                {{ group.title }}
              </h5>
              <p class="mb-0">
                {{ group.description }}
              </p>
            </BMediaBody>
          </BMedia>
        </FrCardCheckboxInput>
      </div>
    </template>
  </BRow>
</template>

<script>
import { pull, cloneDeep } from 'lodash';
import {
  BMedia,
  BMediaBody,
  BRow,
} from 'bootstrap-vue';
import FrIcon from '@forgerock/platform-shared/src/components/Icon';
import FrCardCheckboxInput from '@forgerock/platform-shared/src/components/CardCheckboxInput/';
import { mapState } from 'pinia';
import { useUserStore } from '@forgerock/platform-shared/src/stores/user';
/**
 * Component for choosing admin type
 * Options are `super-admins` and `tenant-admins`
*/
export default {
  name: 'AdminGroupSelection',
  components: {
    BMedia,
    BMediaBody,
    BRow,
    FrIcon,
    FrCardCheckboxInput,
  },
  props: {
    initialAdminGroups: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedAdminGroups: cloneDeep(this.initialAdminGroups),
      adminGroups: [
        {
          name: 'super-admins',
          icon: 'manage_accounts',
          iconColor: 'green',
          title: this.$t('email.inviteAdmin.superAdmin'),
          description: this.$t('email.inviteAdmin.superAdminsText'),
        },
        {
          name: 'tenant-admins',
          icon: 'supervisor_account',
          iconColor: 'blue',
          title: this.$t('tenantSettings.federation.tenantAdmin'),
          description: this.$t('email.inviteAdmin.tenantAdminsText'),
        },
      ],
    };
  },
  computed: {
    ...mapState(useUserStore, ['idmUIAdminRoles']),
  },
  mounted() {
    const tenantAuditorAvailable = JSON.stringify(this.idmUIAdminRoles).includes('tenant-auditor');
    const brandAdminAvailable = JSON.stringify(this.idmUIAdminRoles).includes('brand-admin');
    if (tenantAuditorAvailable) {
      this.adminGroups.push({
        name: 'tenant-auditor',
        icon: 'supervisor_account',
        iconColor: 'orange',
        title: this.$t('tenantSettings.federation.tenantAuditor'),
        description: this.$t('email.inviteAdmin.tenantAuditorText'),
      });
    }
    if (brandAdminAvailable) {
      this.adminGroups.push({
        name: 'brand-admin',
        icon: 'supervisor_account',
        iconColor: 'purple',
        title: this.$t('tenantSettings.federation.brandAdmin'),
        description: this.$t('email.inviteAdmin.brandAdminText'),
      });
    }
  },
  methods: {
    setGroup(value, groupName) {
      if (value && (groupName === 'super-admins' || groupName === 'tenant-admins')) {
        // If the group is super-admin or tenant-admin, remove all other groups
        this.selectedAdminGroups = [groupName];
      } else if (value && !this.selectedAdminGroups.includes(groupName)) {
        // If the group is selected and not already in the array, add it
        this.selectedAdminGroups.push(groupName);
        // always pull super-admins and tenant-admins if any other group is selected
        pull(this.selectedAdminGroups, 'super-admins');
        pull(this.selectedAdminGroups, 'tenant-admins');
      } else if (!value && this.selectedAdminGroups.includes(groupName)) {
        // If the group is deselected and already in the array, remove it
        pull(this.selectedAdminGroups, groupName);
      }

      this.$emit('change-admin-groups', this.selectedAdminGroups);
    },
  },
};
</script>
  <style lang="scss" scoped>
    .fr-icon-container {
      width: 42px;
      height: 42px;
    }
    :deep(.fr-card-clickable) { height: 100%; }
</style>
